import { eventBus, events } from '@/eventBus.js';
import { useModalStack, visitModal as visit } from '@inertiaui/modal-vue';
import { watch } from 'vue';

type ModalInstance = { close: () => void } | null;

type VisitModalOptions = Record<string, any> & { history?: boolean };

export const visitModal = (url: string, options: VisitModalOptions = {}) => {
    const { history = false, ...visitOptions } = options;

    if (history && url) {
        return visitModalWithHistory(url, visitOptions);
    }

    return visit(url, visitOptions).catch(flashError);
};

/**
 * Reflect the modal URL in the address bar without an Inertia visit:
 * push history on open, revert via `history.back()` when the modal closes
 * via the close button or escape, and close the modal when the user
 * navigates back via the browser (the URL is already reverted then).
 */
const visitModalWithHistory = (url: string, options: Record<string, any>) => {
    // inertiaui-modal captures its base URL from window.location.href when the root
    // modal opens; that base URL drives the `back()` redirect target (via the
    // X-InertiaUI-Modal-Base-Url header). Because we pushState the modal URL *before*
    // opening, the package would otherwise record the modal's own URL as the base —
    // so a server `back()` after e.g. deleting the underlying record redirects onto
    // the now-gone modal route. Capture the real origin here and restore it once the
    // modal opens (only when this is the root modal, matching the package's own rule).
    const { stack, setBaseUrl } = useModalStack();
    const origin = window.location.href;
    const isRootModal = stack.value.length === 0;

    window.history.pushState({ urlAwareModalOpen: true }, '', url);

    let modalInstance: ModalInstance = null;
    let cleanedUp = false;
    let poppedBeforeOpen = false;

    const onPopState = () => {
        // A back-navigation can fire before visit() resolves, when there is no modalInstance to
        // close yet. Record it so the .then handler closes the modal as soon as it opens, instead
        // of leaving it open and out of sync with the (already reverted) URL.
        poppedBeforeOpen = true;
        modalInstance?.close();
    };

    const cleanupHistory = () => {
        if (cleanedUp) {
            return;
        }
        cleanedUp = true;
        window.removeEventListener('popstate', onPopState);
        if (window.history.state?.urlAwareModalOpen) {
            window.history.back();
        }
    };

    const originalOnClose = options.onClose;
    const originalOnError = options.onError;

    window.addEventListener('popstate', onPopState);

    return visit(url, {
        ...options,
        onClose: (...args: unknown[]) => {
            cleanupHistory();
            originalOnClose?.(...args);
        },
        onError: (...args: unknown[]) => {
            cleanupHistory();
            originalOnError?.(...args);
        },
    })
        .then((modal: ModalInstance) => {
            modalInstance = modal;
            if (isRootModal) {
                setBaseUrl(origin);
            }
            if (poppedBeforeOpen) {
                // The back-navigation already reverted the URL before the modal resolved. Close it
                // and run cleanupHistory() so the popstate listener is removed even when there was
                // no ModalInstance for onPopState to close (otherwise the listener leaks).
                modal?.close();
                cleanupHistory();
            }
            return modal;
        })
        .catch((response: any) => {
            cleanupHistory();
            flashError(response);
        });
};

const flashError = (response: any) => {
    const error = response?.response?.data?.error;
    if (!error) return;

    eventBus.$emit(events.presentFlashMessage, {
        payload: {
            message: error,
            status: 'error',
        },
    });
};

/**
 * Replace the currently-open modal with a new one: close the current modal,
 * wait for it to fully unmount, then open the target modal. Avoids stacking.
 *
 * The wait is required because inertiaui-modal clears the stack only after
 * the index-0 modal finishes its leave transition; opening earlier would
 * cause the new modal to be wiped out alongside the closing one.
 */
export const replaceModal = (currentModal: { close: () => void } | null, url: string, options?: VisitModalOptions) => {
    if (!currentModal) {
        return visitModal(url, options);
    }

    const { stack } = useModalStack();

    return new Promise((resolve) => {
        const stop = watch(
            () => stack.value.length,
            (length) => {
                if (length === 0) {
                    stop();
                    resolve(visitModal(url, options));
                }
            },
        );

        currentModal.close();
    });
};
